import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" data-testid="forgot-password-page">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2"><EnvelopeSimple size={20} className="text-primary" /></div>
            <CardTitle>Reset your password</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="forgot-sent">
                If <span className="font-medium">{email}</span> is registered, a password-reset link is on its way. It&apos;s valid for 60 minutes.
              </div>
              <p className="text-xs text-muted-foreground">
                Check your spam folder if you don&apos;t see it in 2–3 minutes.
              </p>
              <Button onClick={() => nav("/login")} variant="outline" className="w-full">
                <ArrowLeft size={14} className="mr-1.5" /> Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email" required autoFocus
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the email you use to sign in. We&apos;ll send a link to reset the password
                on every workspace this email owns.
              </p>
              <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <Link to="/login" className="block text-center text-xs text-primary hover:underline">
                Back to sign in
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
